// @flow
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
// selectors
import * as selectors from '../../home/selectors';
import { initiatingCheckoutLoadingSelector } from '../selectors';
import { loggedInSelector } from '../../user/selectors';
// Custom hooks
import { useUtmManager } from '../../common/customHooks/useUtmManager';
// Helpers
import { priceConverter, formatCurrency } from '../../common';
// Analytics
import * as Analytics from '../../common/analytics';
// Assets
import OrderIcon from '../images/order-icon.svg';
import { jsConfetti } from '../../App';
import Arrow from '../images/arrow.svg';
// Types
import type { BigClubInfo, LineItem } from '../types';
// Styles
import './CartResumePreview.css';

type Props = {
  totalPrice: number,
  hasProductsDiscontinuedOrOutOfStock: boolean,
  items: LineItem[],
  theme: string,
  bigClubInfo: BigClubInfo,
  handleInitiateCheckout: () => void,
  onCheckoutFail: () => void,
  initiatingCheckoutLoading: boolean,
  loggedIn: boolean,
  userId: ?number,
  orderNumber: ?string,
  orderToken: ?string,
};

const CartResumePreview = (props: Props) => {
  const history = useHistory();
  const { forceVisitorLogUpdate } = useUtmManager({
    userId: props.userId,
    orderNumber: props.orderNumber,
    orderToken: props.orderToken,
  });

  const totalPrice = props.bigClubInfo?.available
    ? props.totalPrice - props.bigClubInfo?.available
    : props.totalPrice;

  const [showPriceList, setShowPriceList] = useState(false);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCheckoutClick = async e => {
    Analytics.logTappedCheckout(props.items);
    if (props.theme === 'carnival') {
      jsConfetti.addConfetti();
    }

    if (!props.loggedIn) {
      history.push({
        pathname: '/login',
        state: {
          to: '/checkout/',
          message: 'Por favor entre ou crie sua conta para continuar',
        },
      });
      return;
    }

    const response = await props.handleInitiateCheckout();

    if (response && response.result) {
      forceVisitorLogUpdate();
      history.push('/checkout/');
    } else {
      props.onCheckoutFail();
    }
  };

  return (
    <section className={`cart-resume-preview-container fixed`}>
      {(isDesktop || showPriceList) && (
        <>
          <div className="cart-resume-preview-header">
            <img src={OrderIcon} alt="OrderIcon"></img>
            <h3 className="cart-resume-preview-title">Resumo do pedido</h3>
          </div>
          <div className="cart-resume-preview-info">
            <p className="cart-resume-preview-info-text">Itens</p>
            <span className="cart-resume-preview-info-text-alt">
              {formatCurrency(props.totalPrice)}
            </span>
          </div>
          {props.bigClubInfo?.available > 0 && (
            <div className="cart-resume-preview-info">
              <p className="cart-resume-preview-info-text">Saldo BigClub</p>
              <span className="cart-resume-preview-big-club-text-alt">
                - {priceConverter(props.bigClubInfo?.available)}
              </span>
            </div>
          )}
          {totalPrice > 150 && (
            <div className="cart-resume-preview-info">
              <p className="cart-resume-preview-info-text">Taxa de Entrega</p>
              <span className="cart-resume-preview-info-text-alt-medium">
                R$ 00,00
              </span>
            </div>
          )}
          <hr className="cart-resume-preview-horizontal-rule"></hr>
          <div className="cart-resume-previewinfo">
            <p className="cart-resume-preview-text">Total:</p>
            <span className="cart-resume-preview-info-text-alt cart-resume-preview-info-text-orange">
              {formatCurrency(totalPrice)}
            </span>
          </div>
        </>
      )}
      {isDesktop ? (
        <div
          className={`cart-resume-preview-button ${
            props.hasProductsDiscontinuedOrOutOfStock ||
            props.initiatingCheckoutLoading
              ? 'cart-list-button-disabled'
              : ''
          }`}
          onClick={() => {
            if (props.initiatingCheckoutLoading) return;
            Analytics.logTappedCheckout(props.items);
            props.theme === 'carnival' && jsConfetti.addConfetti();
            handleCheckoutClick();
          }}
        >
          FECHAR COMPRA{' '}
          {props.initiatingCheckoutLoading && (
            <i className="fa fa-spinner fa-pulse fa-1x" aria-hidden="true" />
          )}
        </div>
      ) : (
        <div className="cart-resume-preview-button-small-container">
          <span className="cart-resume-preview-info-text-alt ">
            {priceConverter(parseFloat(Math.abs(totalPrice)))}
          </span>
          <div className="cart-resume-buttons-container">
            <div
              className={`cart-resume-preview-button-small ${
                props.hasProductsDiscontinuedOrOutOfStock ||
                props.initiatingCheckoutLoading
                  ? 'cart-list-button-disabled'
                  : ''
              }`}
              onClick={() => {
                if (props.initiatingCheckoutLoading) return;
                Analytics.logTappedCheckout(props.items);
                props.theme === 'carnival' && jsConfetti.addConfetti();
                handleCheckoutClick();
              }}
            >
              FECHAR COMPRA{' '}
              {props.initiatingCheckoutLoading && (
                <i
                  className="fa fa-spinner fa-pulse fa-1x"
                  aria-hidden="true"
                />
              )}
            </div>
            <div
              className="cart-resume-preview-icon"
              onClick={() => {
                setShowPriceList(!showPriceList);
              }}
            >
              <img src={Arrow} alt="Arrow" />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    theme: selectors.themeSelector(state),
    initiatingCheckoutLoading: initiatingCheckoutLoadingSelector(state),
    loggedIn: loggedInSelector(state),
  };
};

const connector: Connector<{}, Props> = connect(mapStateToProps);
export default connector(CartResumePreview);
